import {EXTRA_FORM_NAMES, FORM_NAMES, FormTypes} from '../../utils/guestForm';
import {IdentityVerificationPayload} from 'hooks';
import {
  CreateGuestPayload,
  CustomForm,
  getPhoneNumberString,
  isActiveFormField,
  PAYLOAD_FIELDS_NAMES,
  SelectOptionType,
} from '@guestapp/sdk';
import {getPayloadDate, getSignaturePayLoadValue} from '../../utils/common';
import {AddFormType, AddFormV2Type} from 'context/guest';
import {encodeBase64Url, tryDecodeBase64Url} from 'utils/encoding';
import {STANDARD_FIELD_V2_TYPES} from '@guestapp/core';

const NOT_ACTIVE_FIELDS_EXCEPTIONS = [
  PAYLOAD_FIELDS_NAMES.document_expedition_municipality,
  PAYLOAD_FIELDS_NAMES.residence_municipality,
  PAYLOAD_FIELDS_NAMES.birth_place_municipality,
];

type BuildGuestPayloadArgs = {
  reservationId?: string;
  data: FormTypes;
  signature: string;
  customForm: CustomForm;
  identityVerificationPayload?: IdentityVerificationPayload;
  formFields: AddFormType | AddFormV2Type;
};

export const buildCustomFieldsPayload = (customForm: CustomForm, data: FormTypes) => {
  const customFieldsPayload: {[key: string]: string | null | Blob} = {};

  const customFields = customForm.fields_set
    .filter(field => field.field.is_custom)
    .map(customField => {
      return {name: customField.field.name, type: customField.field.field_type};
    });
  const encodedKeys: string[] = [];
  for (const customFieldName of customFields) {
    encodedKeys.push(customFieldName.name);
    customFieldsPayload[tryDecodeBase64Url(customFieldName.name)] =
      data[customFieldName.name]?.value || data[customFieldName.name];
  }

  return {payload: customFieldsPayload, encodedKeys};
};

const buildCustomFieldsV2Payload = (data: FormTypes, formFields: AddFormV2Type) => {
  const customFieldTypeMap = new Map(
    Object.values(formFields)
      .filter(({is_custom}) => is_custom)
      .map(({name, type}) => [encodeBase64Url(name), type]),
  );

  const customFieldNames = Object.keys(data).filter(name => customFieldTypeMap.has(name));
  const payload: Record<string, unknown> = {};

  for (const customFieldName of customFieldNames) {
    const type = customFieldTypeMap.get(customFieldName);
    if (
      type === STANDARD_FIELD_V2_TYPES.time ||
      type === STANDARD_FIELD_V2_TYPES.select
    ) {
      payload[tryDecodeBase64Url(customFieldName)] = data[customFieldName]?.value;
    } else if (type === STANDARD_FIELD_V2_TYPES.date) {
      payload[tryDecodeBase64Url(customFieldName)] = getPayloadDate(
        data[customFieldName],
      );
    } else if (type === STANDARD_FIELD_V2_TYPES.file) {
      payload[tryDecodeBase64Url(customFieldName)] = data[customFieldName] as File;
    } else {
      payload[tryDecodeBase64Url(customFieldName)] = data[customFieldName];
    }
  }

  return {payload, encodedKeys: customFieldNames};
};

const clearValue = <T, D>(value: T, transformedValue?: D) => {
  if (!value) {
    return;
  }

  return transformedValue || value;
};

const buildExtraFormFields = (data: FormTypes) => {
  const extraFormFields: {[key: string]: EXTRA_FORM_NAMES} = {};
  if (data[EXTRA_FORM_NAMES.send_copy_contract_email]) {
    extraFormFields[EXTRA_FORM_NAMES.send_copy_contract_email] =
      data[EXTRA_FORM_NAMES.send_copy_contract_email];
  }
  return extraFormFields;
};

const getNameAndCodeOfCities = (formValue?: SelectOptionType | string | null) => {
  const name = typeof formValue === 'string' ? formValue : formValue?.label;
  const code = typeof formValue === 'string' ? undefined : formValue?.value;

  return {name, code};
};

type CreateGuestPayloadWithoutReservationId = Omit<CreateGuestPayload, 'reservation_id'>;

const removeEncodedCustomFieldKeys = (
  data: Record<string, unknown>,
  encodedKeys: string[],
): Record<string, unknown> => {
  const encodedKeysSet = new Set(encodedKeys);
  return Object.keys(data).reduce((acc, key) => {
    if (!encodedKeysSet.has(key)) {
      acc[key] = data[key];
    }
    return acc;
  }, {} as Record<string, unknown>);
};

const removeNotActiveFields =
  (formFields: AddFormV2Type | AddFormType, exceptions: string[] = []) =>
  <T extends CreateGuestPayloadWithoutReservationId & Record<string, unknown>>(
    data: T,
  ): T => {
    const filteredData = {...data};

    for (const fieldName of Object.keys(formFields).filter(
      fieldName => !exceptions.includes(fieldName),
    )) {
      if (!isActiveFormField(formFields[fieldName])) {
        delete filteredData[fieldName];
      }
    }
    return filteredData;
  };

export const buildGuestPayload = ({
  data,
  signature,
  customForm,
  identityVerificationPayload,
  formFields,
}: BuildGuestPayloadArgs): Omit<CreateGuestPayload, 'reservation_id'> => {
  const birthDate = getPayloadDate(data[FORM_NAMES.birth_date]);
  const issueDate = getPayloadDate(data[FORM_NAMES.document_issue_date]);
  const documentExpirationDate = getPayloadDate(
    data[FORM_NAMES.document_expiration_date],
  );

  const signatureValue = getSignaturePayLoadValue(signature);
  const {payload: customFieldsPayload, encodedKeys} = customForm
    ? buildCustomFieldsPayload(customForm, data)
    : buildCustomFieldsV2Payload(data, formFields as AddFormV2Type);

  const extraFormFields = buildExtraFormFields(data);

  const {name: birthPlaceCityName, code: birthPlaceCityCode} = getNameAndCodeOfCities(
    data[FORM_NAMES.birth_place_city],
  );

  const {name: residenceCityName, code: residenceCityCode} = getNameAndCodeOfCities(
    data[FORM_NAMES.residence_city],
  );

  const {name: nextDestinationMunicipalityName, code: nextDestinationMunicipalityCode} =
    getNameAndCodeOfCities(data[FORM_NAMES.next_destination_municipality]);

  const {name: documentExpeditionCityName, code: documentExpeditionCityCode} =
    getNameAndCodeOfCities(data[FORM_NAMES.document_expedition_city]);

  const {name: arrivalFromMunicipalityName, code: arrivalFromMunicipalityCode} =
    getNameAndCodeOfCities(data[FORM_NAMES.arrived_from_municipality]);

  const phone = getPhoneNumberString(data[FORM_NAMES.phone]);
  const secondPhone = getPhoneNumberString(data[FORM_NAMES.second_phone]);
  const email = data[FORM_NAMES.email] || data[FORM_NAMES.contract_email];

  const {agreement_clauses, ...restData} = data;

  const restDataWithoutEncodedKeys = removeEncodedCustomFieldKeys(restData, encodedKeys);

  return removeNotActiveFields(
    formFields,
    NOT_ACTIVE_FIELDS_EXCEPTIONS,
  )({
    ...restDataWithoutEncodedKeys,
    ...agreement_clauses,
    [PAYLOAD_FIELDS_NAMES.name]: clearValue(data[FORM_NAMES.name]) as string,
    [PAYLOAD_FIELDS_NAMES.surname]: clearValue(data[FORM_NAMES.surname]) as string,
    [PAYLOAD_FIELDS_NAMES.gender]: clearValue(data[FORM_NAMES.gender]?.value),
    [PAYLOAD_FIELDS_NAMES.second_surname]: clearValue(data[FORM_NAMES.second_surname]),
    [PAYLOAD_FIELDS_NAMES.birth_date]: clearValue(
      data[FORM_NAMES.birth_date],
      birthDate,
    ) as string,
    [PAYLOAD_FIELDS_NAMES.document_expiration_date]: clearValue(
      data[FORM_NAMES.document_expiration_date],
      documentExpirationDate,
    ) as string,
    [PAYLOAD_FIELDS_NAMES.nationality]: clearValue(data[FORM_NAMES.nationality]?.value),
    [PAYLOAD_FIELDS_NAMES.document_type]: clearValue(
      data[FORM_NAMES.document_type]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.document_number]: clearValue(data[FORM_NAMES.document_number]),
    [PAYLOAD_FIELDS_NAMES.visa_number]: clearValue(data[FORM_NAMES.visa_number]),
    [PAYLOAD_FIELDS_NAMES.document_issue_date]: clearValue(
      data[FORM_NAMES.document_issue_date],
      issueDate,
    ),
    [PAYLOAD_FIELDS_NAMES.birth_place_city]: clearValue(birthPlaceCityName),
    [PAYLOAD_FIELDS_NAMES.birth_place_municipality]: clearValue(birthPlaceCityCode),
    [PAYLOAD_FIELDS_NAMES.birth_place_country]: clearValue(
      data[FORM_NAMES.birth_place_country]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.birth_place_address]: clearValue(
      data[FORM_NAMES.birth_place_address],
    ),
    [PAYLOAD_FIELDS_NAMES.residence_province]: clearValue(
      data[FORM_NAMES.residence_province]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.residence_country]: clearValue(
      data[FORM_NAMES.residence_country]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.residence_community]: clearValue(
      data[FORM_NAMES.residence_community]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.residence_city]: clearValue(residenceCityName),
    [PAYLOAD_FIELDS_NAMES.residence_municipality]: clearValue(residenceCityCode),
    [PAYLOAD_FIELDS_NAMES.document_expedition_city]: clearValue(
      documentExpeditionCityName,
    ),
    [PAYLOAD_FIELDS_NAMES.document_expedition_municipality]: clearValue(
      documentExpeditionCityCode,
    ),
    [PAYLOAD_FIELDS_NAMES.document_expedition_country]: clearValue(
      data[FORM_NAMES.document_expedition_country]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.residence_address]: clearValue(
      data[FORM_NAMES.residence_address],
    ),
    [PAYLOAD_FIELDS_NAMES.next_destination_country]: clearValue(
      data[FORM_NAMES.next_destination_country]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.next_destination_district]: clearValue(
      data[FORM_NAMES.next_destination_district]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.next_destination_municipality]: clearValue(
      nextDestinationMunicipalityName,
      nextDestinationMunicipalityCode,
    ),
    [PAYLOAD_FIELDS_NAMES.residence_postal_code]: clearValue(
      data[FORM_NAMES.residence_postal_code],
    ),
    [PAYLOAD_FIELDS_NAMES.citizenship]: clearValue(data[FORM_NAMES.citizenship]?.value),
    [PAYLOAD_FIELDS_NAMES.fiscal_code]: clearValue(data[FORM_NAMES.fiscal_code]),
    [PAYLOAD_FIELDS_NAMES.purpose_of_stay]: clearValue(
      data[FORM_NAMES.purpose_of_stay]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.arrived_from_country]: clearValue(
      data[FORM_NAMES.arrived_from_country]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.arrived_from_district]: clearValue(
      data[FORM_NAMES.arrived_from_district]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.arrived_from_municipality]: clearValue(
      arrivalFromMunicipalityName,
      arrivalFromMunicipalityCode,
    ),
    [PAYLOAD_FIELDS_NAMES.document_support_number]: clearValue(
      data[FORM_NAMES.document_support_number],
    ),
    [PAYLOAD_FIELDS_NAMES.kinship_relationship]: clearValue(
      data[FORM_NAMES.kinship_relationship]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.phone]: clearValue(phone),
    [PAYLOAD_FIELDS_NAMES.second_phone]: clearValue(secondPhone),
    [PAYLOAD_FIELDS_NAMES.email]: clearValue(email),
    [PAYLOAD_FIELDS_NAMES.tax_exemption]: clearValue(
      data[FORM_NAMES.tax_exemption]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.full_tourist_tax]: clearValue(
      data[FORM_NAMES.full_tourist_tax],
    ),
    [PAYLOAD_FIELDS_NAMES.next_destination_address]: clearValue(
      data[FORM_NAMES.next_destination_address],
    ),
    [PAYLOAD_FIELDS_NAMES.signature]: clearValue(signatureValue),
    [PAYLOAD_FIELDS_NAMES.external_category_id]: clearValue(
      data[FORM_NAMES.external_category_id]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.invoice_leader_type]: clearValue(
      data[FORM_NAMES.invoice_leader_type]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.recipient_code_type]: clearValue(
      data[FORM_NAMES.recipient_code_type]?.value,
    ),
    [PAYLOAD_FIELDS_NAMES.recipient_code]: clearValue(data[FORM_NAMES.recipient_code]),
    [PAYLOAD_FIELDS_NAMES.vat_number]: clearValue(data[FORM_NAMES.vat_number]),
    [PAYLOAD_FIELDS_NAMES.chip_card]: clearValue(data[FORM_NAMES.chip_card]),
    [PAYLOAD_FIELDS_NAMES.is_leader]: data?.is_leader,
    [PAYLOAD_FIELDS_NAMES.is_iv_failed]: data?.is_iv_failed,
    ...identityVerificationPayload,
    ...customFieldsPayload,
    ...extraFormFields,
  });
};
